'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { DASHBOARD_TAG_QUERIES, createDirectoryViewModel } from './selectors';
import type {
  DirectoryFilters,
  DirectoryOpportunity,
  NSFAward,
  NSFAwardDetail,
  REUSite,
  ResearchData,
} from './types';

export const initialResearchFilters: DirectoryFilters = {
  sourceTab: 'rutgers',
  searchQuery: '',
  department: '',
  programType: '',
  campus: '',
  subject: '',
  compensation: null,
  includeConcluded: false,
  includeREU: false,
  includeNSF: false,
  includeREUSites: false,
  sortBy: 'relevance',
  sortOrder: 'asc',
  page: 1,
};

async function parseREUSites(csvText: string): Promise<REUSite[]> {
  const Papa = (await import('papaparse')).default;
  const parsed = Papa.parse<Record<string, string>>(csvText, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (header) => header.trim(),
  });

  return parsed.data
    .map((row, index) => ({
      id: row['NSF Award Number'] || row['Site ID'] || `reu-${index}`,
      title: row.Title || '',
      institution: row.Institution || '',
      site_website: row['Site Website'] || '',
      site_location: row['Site Location (State/Territory/International)'] || '',
      institution_city: row['Institution City'] || '',
      institution_state: row['Institution State/Territory'] || '',
      institution_postal_code: row['Institution Postal Code'] || '',
      additional_location_info: row['Additional Location Information'] || '',
      institution_department: row['Institution Department'] || '',
      research_areas: row['Research Areas'] || '',
      research_topics_keywords: row['Research Topics/Keywords'] || '',
      site_description: row['Site Description'] || '',
      comments: row.Comments || '',
      primary_contact_name: row['Primary Contact Name'] || '',
      primary_contact_email: row['Primary Contact Email'] || '',
      primary_contact_phone: row['Primary Contact Phone'] || '',
      secondary_contact_name: row['Secondary Contact Name'] || '',
      secondary_contact_email: row['Secondary Contact Email'] || '',
      secondary_contact_phone: row['Secondary Contact Phone'] || '',
      other_information: row['Other Information'] || '',
      primary_nsf_funding_unit: row['Primary NSF Funding Unit'] || '',
      nsf_co_funders: row['NSF Co-Funders'] || '',
      external_co_funders: row['External Co-Funders'] || '',
      nsf_award_number: row['NSF Award Number'] || '',
      record_last_updated_date: row['Record Last Updated Date'] || '',
      record_expiration_date: row['Record Expiration Date'] || '',
    }))
    .filter((site) => Boolean(site.title));
}

export function useResearchOpportunitiesController(
  tag: string | null,
  options: { includeAllSources?: boolean } = {},
) {
  const [data, setData] = useState<ResearchData | null>(null);
  const [error, setError] = useState<string>();
  const [reloadKey, setReloadKey] = useState(0);
  const [filters, setFilters] = useState<DirectoryFilters>(() => ({
    ...initialResearchFilters,
    sourceTab: options.includeAllSources ? undefined : 'rutgers',
    includeNSF: options.includeAllSources ? true : initialResearchFilters.includeNSF,
    includeREUSites: options.includeAllSources ? true : initialResearchFilters.includeREUSites,
  }));
  const [searchInput, setSearchInput] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [awards, setAwards] = useState<NSFAward[]>([]);
  const [sites, setSites] = useState<REUSite[]>([]);
  const [nsfLoading, setNsfLoading] = useState(false);
  const [reuLoading, setReuLoading] = useState(false);
  const [nsfLoaded, setNsfLoaded] = useState(false);
  const [reuLoaded, setReuLoaded] = useState(false);
  const [nsfError, setNsfError] = useState<string>();
  const [reuError, setReuError] = useState<string>();
  const [selected, setSelected] = useState<DirectoryOpportunity>();
  const [nsfDetail, setNsfDetail] = useState<NSFAwardDetail>();
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState<string>();

  const setFilter = useCallback(<K extends keyof DirectoryFilters>(
    key: K,
    value: DirectoryFilters[K],
  ) => {
    setFilters((current) => ({
      ...current,
      [key]: value,
      ...(key === 'sourceTab' ? { includeNSF: value === 'nsf-award', includeREUSites: value === 'reu-site', department: '', campus: '', programType: '', subject: '', compensation: null, paidOnly: false, creditOnly: false } : {}),
      page: key === 'page' ? value as number : 1,
    }));
  }, []);

  const applySearch = useCallback(() => {
    setFilter('searchQuery', searchInput);
  }, [searchInput, setFilter]);

  const clearFilters = useCallback(() => {
    setFilters((current) => ({
      ...initialResearchFilters,
      sourceTab: current.sourceTab,
      includeNSF: current.includeNSF,
      includeREUSites: current.includeREUSites,
    }));
    setSearchInput('');
  }, []);

  const retry = useCallback(() => {
    setError(undefined);
    setData(null);
    setReloadKey((current) => current + 1);
  }, []);

  const closeDetail = useCallback(() => setSelected(undefined), []);

  useEffect(() => {
    if (!tag) return;
    const query = DASHBOARD_TAG_QUERIES[tag] || tag;
    setSearchInput(query);
    setFilters((current) => ({ ...current, searchQuery: query, page: 1 }));
  }, [tag]);

  useEffect(() => {
    const abort = new AbortController();

    async function loadRutgersSnapshots() {
      try {
        const activeResponse = await fetch('/research_opportunities_active.json', {
          signal: abort.signal,
        });
        if (!activeResponse.ok) {
          throw new Error('Failed to load active Rutgers research opportunities');
        }
        const activeData = await activeResponse.json() as ResearchData;

        let concludedData: ResearchData | null = null;
        try {
          const concludedResponse = await fetch(
            '/data/research_opportunities_concluded.json',
            { signal: abort.signal },
          );
          if (concludedResponse.ok) {
            concludedData = await concludedResponse.json() as ResearchData;
          }
        } catch {
          if (abort.signal.aborted) return;
          // The concluded snapshot is optional; active projects remain useful.
        }

        if (abort.signal.aborted) return;
        const activeProjects = activeData.projects.map((project) => ({
          ...project,
          is_concluded: false,
        }));
        const concludedProjects = concludedData?.projects.map((project) => ({
          ...project,
          is_concluded: true,
        })) ?? [];
        setData({
          metadata: {
            total_projects: activeProjects.length + concludedProjects.length,
            scraped_date: activeData.metadata.scraped_date,
          },
          projects: [...activeProjects, ...concludedProjects],
        });
        setError(undefined);
      } catch (caught) {
        if (!abort.signal.aborted) {
          setError(caught instanceof Error
            ? caught.message
            : 'Failed to load research opportunities');
        }
      }
    }

    void loadRutgersSnapshots();
    return () => abort.abort();
  }, [reloadKey]);

  useEffect(() => {
    if (!filters.includeNSF || (nsfLoaded && !nsfError)) return;
    const abort = new AbortController();
    setNsfLoading(true);
    setNsfError(undefined);

    void fetch('/api/nsf-awards?limit=1000', { signal: abort.signal })
      .then((response) => (
        response.ok
          ? response.json()
          : Promise.reject(new Error('NSF awards unavailable'))
      ))
      .then((result: { awards?: NSFAward[] }) => {
        if (abort.signal.aborted) return;
        setAwards(result.awards ?? []);
        setNsfLoaded(true);
      })
      .catch((caught: unknown) => {
        if (!abort.signal.aborted) {
          setNsfError(caught instanceof Error ? caught.message : 'NSF awards unavailable');
        }
      })
      .finally(() => {
        if (!abort.signal.aborted) setNsfLoading(false);
      });

    return () => abort.abort();
  }, [filters.includeNSF, reloadKey]);

  useEffect(() => {
    if (!filters.includeREUSites || (reuLoaded && !reuError)) return;
    const abort = new AbortController();
    setReuLoading(true);
    setReuError(undefined);

    void fetch('/data/nsf_reu_sites.csv', { signal: abort.signal })
      .then((response) => (
        response.ok
          ? response.text()
          : Promise.reject(new Error('NSF REU sites unavailable'))
      ))
      .then(parseREUSites)
      .then((loaded) => {
        if (abort.signal.aborted) return;
        setSites(loaded);
        setReuLoaded(true);
      })
      .catch((caught: unknown) => {
        if (!abort.signal.aborted) {
          setReuError(caught instanceof Error ? caught.message : 'NSF REU sites unavailable');
        }
      })
      .finally(() => {
        if (!abort.signal.aborted) setReuLoading(false);
      });

    return () => abort.abort();
  }, [filters.includeREUSites, reloadKey]);

  useEffect(() => {
    if (selected?.kind !== 'nsf-award' || !selected.award) {
      setNsfDetail(undefined);
      setDetailError(undefined);
      setLoadingDetail(false);
      return;
    }

    const abort = new AbortController();
    const id = selected.award.id.split(',')[0].trim();
    setNsfDetail(undefined);
    setDetailError(undefined);
    setLoadingDetail(true);

    void fetch(`/api/nsf-awards/${encodeURIComponent(id)}`, { signal: abort.signal })
      .then((response) => (
        response.ok
          ? response.json()
          : Promise.reject(new Error('NSF award detail unavailable'))
      ))
      .then((detail: NSFAwardDetail & { error?: string }) => {
        if (abort.signal.aborted) return;
        if (detail.error) throw new Error(detail.error);
        setNsfDetail(detail);
      })
      .catch((caught: unknown) => {
        if (!abort.signal.aborted) {
          setDetailError(caught instanceof Error
            ? caught.message
            : 'NSF award detail unavailable');
        }
      })
      .finally(() => {
        if (!abort.signal.aborted) setLoadingDetail(false);
      });

    return () => abort.abort();
  }, [selected]);

  const state = !data && !error
    ? 'loading'
    : error && !data
      ? 'error'
      : error
        ? 'partial'
        : 'populated';

  const model = useMemo(() => createDirectoryViewModel({
    state,
    error,
    retrievedAt: data?.metadata.scraped_date,
    data,
    awards,
    sites,
    filters,
    selected,
    nsfDetail,
    loadingDetail,
    detailError,
    optionalSources: {
      nsf: { loading: nsfLoading, loaded: nsfLoaded, error: nsfError },
      reu: { loading: reuLoading, loaded: reuLoaded, error: reuError },
    },
  }), [
    awards,
    data,
    detailError,
    error,
    filters,
    loadingDetail,
    nsfDetail,
    nsfError,
    nsfLoaded,
    nsfLoading,
    reuError,
    reuLoaded,
    reuLoading,
    selected,
    sites,
    state,
  ]);

  return {
    model,
    searchInput,
    setSearchInput,
    filtersOpen,
    setFiltersOpen,
    setFilter,
    applySearch,
    clearFilters,
    retry,
    select: setSelected,
    closeDetail,
  };
}
